import React, { useState, useEffect } from 'react';
import {
  Plus, Receipt, Loader2, Filter,
  X, Download, AlertCircle, Search, Pencil
} from 'lucide-react';
import { expenseService } from '../../services/expenseService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const CATEGORIES = [
  'Daily Expense',
  'Connects Purchase',
  'Assets Buy',
  'Subscription',
  'Bank Taxes',
  'Personal Payments',
  'Other',
];

const PERIODS = [
  { value: '', label: 'All Time' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom Range' },
];

const toStr = (d) => d.toISOString().split('T')[0];

function getDateRangeForPeriod(period, customFrom, customTo) {
  const now = new Date();
  if (period === 'weekly') {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'monthly') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'yearly') {
    const start = new Date(now.getFullYear(), 0, 1);
    return { from: toStr(start), to: toStr(now) };
  }
  if (period === 'custom') {
    return { from: customFrom, to: customTo };
  }
  return { from: '', to: '' };
}

// ── Export Modal — asks which period to export ────────────────────────────
const ExportModal = ({ onClose, category, status }) => {
  const [period, setPeriod] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async () => {
    setError('');
    if (period === 'custom' && (!customFrom || !customTo)) {
      return setError('Select both from and to dates.');
    }
    const { from, to } = getDateRangeForPeriod(period, customFrom, customTo);

    setExporting(true);
    try {
      await expenseService.exportExcel(status, category, from, to);
      onClose();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
          Export Expenses
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-control bg-red-50 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">Export For</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary"
            >
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">From</label>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5 tracking-widest">To</label>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-full p-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-primary" />
              </div>
            </div>
          )}

          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download Excel
          </button>
        </div>
      </div>
    </div>
  );
};

const Expenses = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newExpenseCategory, setNewExpenseCategory] = useState(CATEGORIES[0]);
  const [newExpenseOtherCategory, setNewExpenseOtherCategory] = useState('');

  const isFinance = user && (user.role === 'super_admin' || user.role === 'finance_manager');
  // Only Admin, HR and Finance Manager may see/use "Add Expense" - hidden for employees and sales roles.
  const canAddExpense = user && ['super_admin', 'hr_manager', 'finance_manager'].includes(user.role);
  const currentUserId = user?.user_id || user?.id;

  const loadExpenses = async () => {
    setLoading(true);
    try {
      const { from, to } = getDateRangeForPeriod(periodFilter, customFrom, customTo);
      const res = await expenseService.getExpenses(statusFilter, categoryFilter, from, to);
      setExpenses(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadExpenses(); }, [statusFilter, categoryFilter, periodFilter, customFrom, customTo]);

  // Bug fix: when opening the modal, prefill category state from the expense being edited (if any).
  useEffect(() => {
    if (showModal) {
      if (editingExpense) {
        const knownCategories = CATEGORIES.slice(0, -1); // exclude 'Other'
        const isOther = !knownCategories.includes(editingExpense.category);
        setNewExpenseCategory(isOther ? 'Other' : editingExpense.category);
        setNewExpenseOtherCategory(isOther ? editingExpense.category : '');
      } else {
        setNewExpenseCategory(CATEGORIES[0]);
        setNewExpenseOtherCategory('');
      }
    }
  }, [showModal, editingExpense]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target);

    const selectedCategory = formData.get('category') || newExpenseCategory;
    const category = selectedCategory === 'Other'
      ? (newExpenseOtherCategory.trim() || 'Other')
      : selectedCategory;

    // Receipt Base64 handling (only if a new file was chosen)
    const file = e.target.receipt.files[0];
    let base64 = null;
    if (file) {
      const reader = new FileReader();
      base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    }

    const payload = {
      category,
      amount: parseFloat(formData.get('amount')),
      expense_date: formData.get('date'),
      description: formData.get('description'),
      vendor_name: formData.get('vendor'),
      receipt_base64: base64
    };

    try {
      if (editingExpense) {
        await expenseService.updateExpense(editingExpense.expense_id, payload);
      } else {
        await expenseService.submitExpense(payload);
      }
      setShowModal(false);
      setEditingExpense(null);
      loadExpenses();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveReject = async (id, status) => {
    const comments = window.prompt("Add remarks (Optional):");
    try {
      await expenseService.updateStatus(id, status, comments);
      loadExpenses();
    } catch (err) {
      alert(err.message);
    }
  };

  // Bug fix: open the same modal in edit mode for a still-Pending expense.
  const handleEditClick = (exp) => {
    setEditingExpense(exp);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingExpense(null);
  };

  const normalizedSearchTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const visibleExpenses = expenses.filter((exp) => {
    if (!normalizedSearchTerms.length) return true;

    const searchableText = [
      exp.description,
      exp.category,
      exp.vendor_name,
      exp.submitted_by_name,
      exp.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return normalizedSearchTerms.every(term => searchableText.includes(term));
  });

  return (
    <div className="space-y-6 pb-10">
      <style>{`
        .filter-button-gradient {
          background: linear-gradient(135deg, #7c3aed 0%, #9333ea 45%, #ec4899 100%);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 20px 45px rgba(124, 58, 237, 0.18);
          animation: filterButtonRise 0.7s ease-out;
        }

        .filter-select {
          min-width: 150px;
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          font-size: 0.875rem;
          transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .filter-select:hover,
        .filter-select:focus {
          background: #f5f3ff;
          border-color: #c084fc;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.10);
        }

        .filter-bar-row {
          animation: slideUpFade 0.5s ease-out;
        }

        @keyframes slideUpFade {
          from {
            transform: translateY(12px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes filterButtonRise {
          from {
            transform: translateY(12px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Expenses</h1>
          <p className="text-sm text-gray-500">Track company spending and claims</p>
        </div>
        <div className="flex items-center gap-2">
          {isFinance && (
            <button onClick={() => setShowExportModal(true)} className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export
            </button>
          )}
          {canAddExpense && (
            <button onClick={() => { setEditingExpense(null); setShowModal(true); }} className="bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Expense
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar-row bg-white p-2 rounded-[16px] flex flex-wrap items-center gap-2">
        <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)} className="filter-select bg-white outline-none">
          {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="filter-select bg-white outline-none">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {isFinance && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select bg-white outline-none">
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        )}
        <div className="flex items-center gap-2 border border-gray-200 rounded-[14px] px-3 py-2 min-w-[220px] bg-white flex-1">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search description or keyword"
            className="w-full text-sm outline-none bg-transparent"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {visibleExpenses.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed text-gray-400">No expenses matched your search.</div>
          ) : (
            visibleExpenses.map((exp) => {
              const canEdit = exp.status === 'Pending' && (exp.submitted_by === currentUserId || user?.role === 'super_admin');
              return (
                <div key={exp.expense_id} className="bg-white p-6 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-50 rounded-xl text-primary"><Receipt className="w-6 h-6" /></div>
                    <div>
                      <h3 className="font-bold text-gray-900">{exp.category}</h3>
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">{exp.submitted_by_name} &bull; {exp.expense_date}</p>
                      <p className="text-xs text-gray-600 mt-1">{exp.description}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-lg font-black text-gray-900">{exp.amount.toLocaleString()} PKR</p>
                      <div className="mt-1"><StatusBadge status={String(exp.status)} /></div>
                    </div>

                    {canEdit && (
                      <button onClick={() => handleEditClick(exp)} className="p-2 bg-gray-50 text-gray-600 hover:bg-gray-100 rounded-lg transition-all" title="Edit expense">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}

                    {isFinance && exp.status === 'Pending' && (
                      <div className="flex flex-col gap-2">
                        <button onClick={() => handleApproveReject(exp.expense_id, 'Approved')} className="bg-green-600 text-white px-3 py-1 rounded-lg text-[10px] font-bold uppercase">Approve</button>
                        <button onClick={() => handleApproveReject(exp.expense_id, 'Rejected')} className="bg-red-50 text-red-600 px-3 py-1 rounded-lg text-[10px] font-bold uppercase border border-red-100">Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* New / Edit Expense Modal */}
      {canAddExpense && showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
              {editingExpense ? 'Edit Expense' : 'Submit New Expense'}
              <button onClick={handleModalClose}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Category</label>
                  <select
                    name="category"
                    value={newExpenseCategory}
                    onChange={(e) => setNewExpenseCategory(e.target.value)}
                    required
                    className="w-full p-2.5 border rounded-xl text-sm bg-gray-50 outline-none"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {newExpenseCategory === 'Other' && (
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Specify Category</label>
                    <input
                      name="otherCategory"
                      value={newExpenseOtherCategory}
                      onChange={(e) => setNewExpenseOtherCategory(e.target.value)}
                      placeholder="Enter custom category"
                      className="w-full p-2.5 border rounded-xl text-sm bg-gray-50 outline-none"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Amount (PKR)</label>
                  <input name="amount" type="number" step="0.01" required defaultValue={editingExpense ? editingExpense.amount : ''} className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Date</label>
                  <input name="date" type="date" required max="2099-12-31" defaultValue={editingExpense ? editingExpense.expense_date : new Date().toISOString().split('T')[0]} className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Vendor Name</label>
                  <input name="vendor" defaultValue={editingExpense ? (editingExpense.vendor_name || '') : ''} className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Receipt (Image/PDF){editingExpense ? ' — leave empty to keep existing' : ''}</label>
                  <input name="receipt" type="file" accept="image/*,application/pdf" className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Description</label>
                  <textarea name="description" required defaultValue={editingExpense ? editingExpense.description : ''} className="w-full p-3 border rounded-xl text-sm bg-gray-50 h-20 outline-none" />
                </div>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingExpense ? 'Save Changes' : 'Submit for Approval'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          category={categoryFilter}
          status={statusFilter}
        />
      )}
    </div>
  );
};

export default Expenses;